"use client";

import Button from "@/components/ui/Button/Button";
import FAQ from "@/components/ui/FAQ/FAQ";
import Mentor from "../../Library/Mentor/Mentor";
import SelectedMentorBadge from "@/components/Library/Mentor/SelectedMentorBadge/SelectedMentorBadge";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import TranslateDomain from "@/components/TranslateDomain/TranslateDomain";
import { Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";

/**
 * Écran affiché à la place de l'annuaire tant que moins de
 * MENTORING_DIRECTORY_MINIMUM mentors sont réellement réservables
 * (helpers/mentorBookable.js). L'annuaire revient de lui-même au-dessus du
 * seuil : il n'y a rien à rallumer à la main.
 *
 * Parti pris : pas de grille tant qu'il n'y a qu'un mentor. Une grille est une
 * promesse de collection, et la remplir au tiers raconte le manque.
 *
 * La page se remplit avec ce que le produit fait vraiment et que personne ne
 * montrait : le déroulé d'un appel, et des questions dont les réponses sont
 * toutes vérifiables dans le code. RIEN ICI NE PROMET CE QUI N'EXISTE PAS.
 * Deux garde-fous qui ont l'air de détails et n'en sont pas :
 *   - les conditions d'annulation, de remboursement et de durée dépendent de
 *     chaque mentor (et, pour l'appel offert, de l'historique du visiteur, que
 *     cette page publique et mise en cache ne connaît pas) : elles ne sont donc
 *     jamais affirmées ici, seulement renvoyées au profil ;
 *   - aucun taux de commission n'est écrit : le back applique
 *     `getMentorCommissionFees() ?? 10` par mentor, quand le simulateur de
 *     /join-mentors annonce 15 %. Tant que les deux ne disent pas la même
 *     chose, cette page se tait et renvoie vers le simulateur.
 *
 * Enfin, rien n'invente de direction artistique : le titre de page est rendu
 * par page.jsx comme sur les autres catégories, et les titres restent en
 * font-sourceSans, à l'échelle du reste du site.
 */

/* La dalle est sombre dans les DEUX thèmes : c'est la matière exacte du hero
   de la fiche mentor (app/[locale]/(app)/mentoring/[pseudo]/page.jsx:174), pour
   que cliquer sur le portrait ne change pas de monde. */
const DALLE = "linear-gradient(135deg, #171a21 0%, #0b0c10 100%)";

/* Grain : c'est lui qui empêche un dégradé sombre de ressembler à un fond de
   template. Zéro requête, le SVG est en data URI (même procédé que
   ProductShowcase.js:240). */
const GRAIN =
    "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

/* Le déroulé réel d'une réservation. Chaque ligne correspond à quelque chose
   qui existe : l'objet du rendez-vous (champ du tunnel), les trois formats
   d'échange (MeetingPreferenceSelector, clés phone/voice/video stockées en
   base) et le rappel envoyé une heure avant (pages/api/webhooks/call-reminders.js). */
const DEROULE = [
    { cle: "slot", icone: "calendar-mark-linear" },
    { cle: "mode", icone: "videocamera-record-linear" },
    { cle: "reminder", icone: "bell-linear" },
];

const ETAPES = ["step1", "step2", "step3"];

const QUESTIONS = ["price", "how", "cancel", "directory", "become"];

function lienProfil(mentor) {
    return mentor.mentorPseudo
        ? `/mentoring/${mentor.mentorPseudo}`
        : `/mentoring/believer-${mentor.id}`;
}

/**
 * Entrée en scène en CSS et non en JavaScript.
 *
 * Le portrait est l'élément le plus grand de cet écran, donc celui que Google
 * chronomètre. Avec motion, le serveur enverrait la dalle en `opacity: 0` et
 * son apparition attendrait l'hydratation : c'est la raison écrite au-dessus de
 * `.apparition` dans styles/globals.css, où `prefers-reduced-motion` est déjà
 * traité.
 */
function Apparition({ delai = 0, className = "", children }) {
    return (
        <div
            className={`apparition ${className}`}
            style={{
                "--apparition-duree": "0.45s",
                "--apparition-delai": `${delai}s`,
                "--apparition-y": "12px",
            }}
        >
            {children}
        </div>
    );
}

/** Filet : la seule séparation de cet écran, jamais une bordure de carte. */
function Filet({ className = "" }) {
    return (
        <div
            className={`h-px bg-gray-200 dark:bg-white/10 ${className}`}
            aria-hidden="true"
        />
    );
}

function TitreSection({ children }) {
    return (
        <h2 className="font-sourceSans text-xl font-bold tracking-tight text-gray-900 dark:text-white md:text-2xl">
            {children}
        </h2>
    );
}

/**
 * Le mentor réservable, en grand.
 *
 * Les textes posés dessus n'ont pas de variante `dark:` et c'est voulu : la
 * dalle est sombre dans les deux thèmes, une variante les ferait disparaître en
 * mode clair.
 */
function Affiche({ mentor }) {
    const t = useTranslations();
    const minutes = mentor.minutesPerCall;
    const decouverte = !!mentor.mentorAllowsFreeDiscoveryCall;

    // Les domaines réellement cochés par le mentor : une information vraie, et
    // le seul endroit de la dalle qui respire un peu de couleur.
    const domaines = (process.env.MENTOR_DOMAINS || []).filter(
        (d) => mentor[d.domainColumn],
    );

    return (
        <section
            className="relative overflow-hidden rounded-3xl"
            style={{ background: DALLE }}
        >
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0"
                style={{
                    background:
                        "radial-gradient(55% 55% at 88% 12%, rgba(232, 81, 29, 0.16), transparent 70%)",
                }}
            />
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 opacity-[0.035] mix-blend-overlay"
                style={{ backgroundImage: GRAIN }}
            />

            <div className="relative flex flex-col gap-7 p-6 sm:p-8 lg:flex-row lg:items-center lg:gap-12 lg:p-10">
                {/* Portrait. Sur mobile il devient un bandeau : un 3/4 pleine
                    largeur pousserait le bouton à deux écrans du haut. */}
                <Link
                    href={lienProfil(mentor)}
                    className="group block flex-shrink-0 lg:w-[320px]"
                    aria-label={`${mentor.firstName} ${mentor.lastName}`}
                >
                    <div className="relative h-52 overflow-hidden rounded-2xl bg-white/5 sm:h-72 lg:h-auto lg:aspect-[3/4]">
                        {mentor.mentorPicture ? (
                            <div
                                className="absolute inset-0 bg-cover bg-top transition-transform duration-[600ms] ease-out group-hover:scale-105"
                                style={{
                                    backgroundImage: `url(${mentor.mentorPicture})`,
                                }}
                            />
                        ) : (
                            <div className="absolute inset-0 flex items-center justify-center font-sourceSans text-5xl font-bold text-white/30">
                                {(mentor.firstName || "").charAt(0)}
                                {(mentor.lastName || "").charAt(0)}
                            </div>
                        )}
                    </div>
                </Link>

                <div className="min-w-0 flex-1">
                    {/* Le badge maison, déjà en place sur la fiche mentor : il
                        porte sa propre explication en modale. */}
                    <SelectedMentorBadge />

                    <h2 className="mt-4 font-sourceSans text-2xl font-bold tracking-tight text-white md:text-3xl">
                        {mentor.firstName} {mentor.lastName}
                    </h2>

                    {mentor.mentorBio && (
                        <p className="mb-0 mt-2 line-clamp-3 leading-relaxed text-white/60">
                            {mentor.mentorBio}
                        </p>
                    )}

                    {domaines.length > 0 && (
                        <div className="mt-5 flex flex-wrap gap-2">
                            {domaines.map((d) => (
                                <span
                                    key={d.domainColumn}
                                    className="rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white/70"
                                >
                                    <TranslateDomain domain={d.domainName} />
                                </span>
                            ))}
                        </div>
                    )}

                    <div className="mt-7 h-px bg-white/10" aria-hidden="true" />

                    {/* L'offre. Le prix barré reste petit : agrandi, il ferait
                        soldes au lieu de faire geste. */}
                    <div className="mt-6 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                        <span className="font-sourceSans text-xl font-bold text-white">
                            {decouverte
                                ? t("GuestBooking_free")
                                : `${mentor.pricePerCall} €`}
                        </span>
                        {decouverte && mentor.pricePerCall ? (
                            <span className="text-sm text-white/35 line-through">
                                {mentor.pricePerCall} €
                            </span>
                        ) : null}
                    </div>

                    <p className="mb-0 mt-2 text-sm text-white/45">
                        {decouverte && mentor.pricePerCall
                            ? t("mentoring_waitlist_then_price", {
                                  price: mentor.pricePerCall,
                              })
                            : null}
                        {decouverte && mentor.pricePerCall && minutes
                            ? " · "
                            : null}
                        {minutes
                            ? t("BookNewCall_book_point_minutes", {
                                  minutesPerCall: minutes,
                              })
                            : null}
                    </p>

                    {/* Link + Button : le pattern maison. Le bouton apporte la
                        lèvre 3D, et sa couleur vit sur le <button>, hors de
                        portée de la règle a:hover de globals.css qui repeint en
                        rose tout lien coloré au survol. */}
                    <div className="mt-7">
                        <Link href={lienProfil(mentor)}>
                            <Button type="primary" className="press-safe">
                                {t("BookNewCall_button_bookACall")}
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default function MentoringWaitlist({ mentors = [] }) {
    const t = useTranslations();

    // Un seul mentor : il tient l'écran. Deux : leurs deux cartes, contenues,
    // jamais une grille de trois colonnes dont une resterait vide.
    const vedette = mentors.length === 1 ? mentors[0] : null;

    return (
        <div className="mt-8">
            {vedette && (
                <Apparition>
                    <Affiche mentor={vedette} />
                </Apparition>
            )}

            {mentors.length > 1 && (
                <Apparition>
                    <TitreSection>
                        {t("mentoring_waitlist_available_title")}
                    </TitreSection>
                    <div className="mt-5 grid max-w-3xl grid-cols-1 gap-5 sm:grid-cols-2">
                        {mentors.map((mentor) => (
                            <Mentor mentor={mentor} key={mentor.id} />
                        ))}
                    </div>
                </Apparition>
            )}

            {/* Comment se passe un appel. Trois colonnes plutôt qu'une nouvelle
                grille en deux : c'est ce qui évite que la page entière ait le
                même squelette de haut en bas. */}
            {mentors.length > 0 && (
                <Apparition delai={0.08}>
                    <div className="mt-14 md:mt-20">
                        <TitreSection>
                            {t("mentoring_waitlist_how_title")}
                        </TitreSection>
                        <div className="mt-8 grid grid-cols-1 gap-8 sm:grid-cols-3 sm:gap-10">
                            {DEROULE.map((etape) => (
                                <div key={etape.cle}>
                                    <SolarIcon
                                        name={etape.icone}
                                        className="text-2xl text-gray-400 dark:text-grayDark-500"
                                    />
                                    <div className="mt-3 font-semibold text-gray-900 dark:text-white">
                                        {t(
                                            `mentoring_waitlist_how_${etape.cle}_title`,
                                        )}
                                    </div>
                                    <p className="mb-0 mt-1.5 text-sm leading-relaxed text-gray-500 dark:text-grayDark-400">
                                        {t(
                                            `mentoring_waitlist_how_${etape.cle}_text`,
                                        )}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </Apparition>
            )}

            {/* Devenir mentor. C'est la vraie sortie de cet écran : quand aucun
                mentor n'est réservable, c'est même la seule, et son bouton
                reprend alors l'accent orange. */}
            <Apparition delai={0.16}>
                <Filet className="mt-14 md:mt-20" />
                <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-[1fr_320px] lg:gap-16">
                    <div>
                        <TitreSection>
                            {t("mentoring_waitlist_join_title")}
                        </TitreSection>

                        <p className="mb-0 mt-3 max-w-xl leading-relaxed text-gray-600 dark:text-grayDark-300">
                            {t("mentoring_waitlist_join_text")}
                        </p>

                        <div className="mt-7">
                            <Link href="/join-mentors">
                                <Button
                                    type={vedette ? "secondary" : "primary"}
                                    className="press-safe"
                                >
                                    <span className="flex items-center gap-2">
                                        {t("mentoring_become_mentor_cta")}
                                        <SolarIcon
                                            name="arrow-right-linear"
                                            className="text-base"
                                        />
                                    </span>
                                </Button>
                            </Link>
                        </div>
                    </div>

                    {/* Les trois pas, pour que candidater ne soit pas un saut
                        dans le vide. */}
                    <div className="lg:pt-1">
                        {ETAPES.map((etape, index) => (
                            <div key={etape}>
                                {index > 0 && <Filet />}
                                <div className="flex gap-4 py-5">
                                    <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border border-gray-300 text-xs font-semibold text-gray-500 dark:border-white/20 dark:text-grayDark-400">
                                        {index + 1}
                                    </span>
                                    <div className="min-w-0">
                                        <div className="text-sm font-semibold text-gray-900 dark:text-white">
                                            {t(
                                                `mentoring_waitlist_${etape}_title`,
                                            )}
                                        </div>
                                        <p className="mb-0 mt-1 text-sm leading-relaxed text-gray-500 dark:text-grayDark-400">
                                            {t(
                                                `mentoring_waitlist_${etape}_text`,
                                            )}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </Apparition>

            {/* Les questions. Beaucoup de hauteur pour très peu de poids
                visuel, ce qu'il faut en bas d'un écran qui manquait de corps.
                Composant FAQ maison (celui des pages de formation), sans la
                boîte qui l'entoure ailleurs. */}
            <Apparition delai={0.16}>
                <Filet className="mt-14 md:mt-20" />
                <div className="mt-10 max-w-3xl">
                    <TitreSection>
                        {t("mentoring_waitlist_faq_title")}
                    </TitreSection>
                    <div className="mt-4 divide-y divide-gray-200 dark:divide-white/10">
                        {QUESTIONS.map((question, index) => (
                            <FAQ
                                key={question}
                                question={t(
                                    `mentoring_waitlist_faq_${question}_q`,
                                )}
                                first={index === 0}
                                last={index === QUESTIONS.length - 1}
                            >
                                <div className="text-gray-600 dark:text-grayDark-300">
                                    {t(`mentoring_waitlist_faq_${question}_a`)}
                                </div>
                            </FAQ>
                        ))}
                    </div>
                </div>
            </Apparition>
        </div>
    );
}
